import { productConfig } from "./config";

export type SessionIdentity = {
  user: {
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
  };
  organization: {
    id: string;
    type: "personal" | "workos";
    name: string | null;
  };
  membership: {
    id: string;
    role: string;
    status: string;
  };
  csrf_token: string;
};

export type AuthErrorBody = {
  error?: {
    code?: string;
    message?: string;
    requestId?: string;
  };
};

export type SessionResult =
  | { kind: "authenticated"; session: SessionIdentity }
  | { kind: "signed_out" }
  | { kind: "access_not_provisioned"; requestId?: string }
  | { kind: "temporarily_unavailable"; requestId?: string }
  | { kind: "authentication_error"; requestId?: string };

export async function loadSession(signal?: AbortSignal): Promise<SessionResult> {
  try {
    const response = await fetch(`${productConfig.apiOrigin}/v1/auth/session`, {
      method: "GET",
      credentials: "include",
      headers: { accept: "application/json" },
      signal,
    });

    if (response.ok) {
      return { kind: "authenticated", session: (await response.json()) as SessionIdentity };
    }

    const body = await safeErrorBody(response);
    const requestId = body.error?.requestId;

    if (response.status === 401) {
      return { kind: "signed_out" };
    }

    if (response.status === 403) {
      return { kind: "access_not_provisioned", requestId };
    }

    if (response.status === 503) {
      return { kind: "temporarily_unavailable", requestId };
    }

    return { kind: "authentication_error", requestId };
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }

    return { kind: "temporarily_unavailable" };
  }
}

export function submitLogout(csrfToken: string): void {
  const form = document.createElement("form");
  const csrf = document.createElement("input");
  form.method = "POST";
  form.action = `${productConfig.apiOrigin}/auth/logout`;
  form.hidden = true;
  csrf.type = "hidden";
  csrf.name = "_csrf";
  csrf.value = csrfToken;
  form.append(csrf);
  document.body.append(form);
  form.submit();
}

async function safeErrorBody(response: Response): Promise<AuthErrorBody> {
  try {
    return (await response.json()) as AuthErrorBody;
  } catch {
    return {};
  }
}
